import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, AlertTriangle, Tag } from "lucide-react";
import { ComplexityBadge } from "./StatusBadge";
import { localized, localizedList } from "../lib/localized";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";

export const AiAnalysisPanel = ({ ai, loading, error, errorType }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] || "en";

  return (
    <div
      className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-6 dark:border-indigo-900 dark:bg-indigo-950/20"
      data-testid="ai-analysis-panel"
    >
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <h3 className="font-heading text-base font-semibold leading-none tracking-tight">{t("ai.title")}</h3>
            <p className="mt-1 text-xs text-muted-foreground">{t("ai.poweredBy")}</p>
          </div>
        </div>
        {ai?.complexity && <ComplexityBadge complexity={ai.complexity} />}
      </div>

      {loading && (
        <div className="space-y-3" data-testid="ai-loading">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <p className="pt-2 text-sm text-indigo-600">{t("case.aiAnalyzing")}</p>
        </div>
      )}

      {!loading && error && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="ai-error">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{errorType === "quota" ? t("ai.quotaError") : t("ai.genericError")}</span>
        </div>
      )}

      {!loading && !error && !ai && (
        <p className="text-sm text-muted-foreground" data-testid="ai-not-run">{t("ai.notRun")}</p>
      )}

      {!loading && !error && ai && (
        <div className="space-y-4" data-testid="ai-content">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t("ai.description")}</p>
            <p className="text-sm leading-relaxed text-foreground">{localized(ai.description, lang)}</p>
          </div>

          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t("ai.summary")}</p>
            <p className="text-sm italic text-foreground">"{localized(ai.summary, lang)}"</p>
          </div>

          {ai.tags?.length > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                <Tag className="h-3 w-3" /> {t("ai.tags")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {ai.tags.map((tag) => (
                  <Badge key={tag} className="rounded-full bg-indigo-600 text-white hover:bg-indigo-600">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              <AlertTriangle className="h-3 w-3" /> {t("ai.warnings")}
            </p>
            {localizedList(ai.warnings, lang).length > 0 ? (
              <ul className="space-y-1">
                {localizedList(ai.warnings, lang).map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-amber-700">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                    {w}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-emerald-700">{t("ai.noWarnings")}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
